import { Fragment, useEffect, useMemo, useState } from "react";
import { Copy, TrendingUp, Waves } from "lucide-react";
import { toast } from "sonner";
import { ForecastChartPanel } from "./ForecastChartPanel";
import { ForecastRuleEditorModal } from "./ForecastRuleEditorModal";
import { SmartSuggestionsPanel } from "./SmartSuggestionsPanel";
import { useForecastInsights } from "../../hooks/useForecastInsights";
import { useCurrentCurrency } from "../../hooks/useCurrentCurrency";
import { useConsolidated } from "../../hooks/useConsolidated";
import { useForecastProjection } from "../../hooks/useForecastProjection";
import { useCategories } from "../../hooks/useCategories";
import type { CategoryItem } from "../../types";
import type {
  ForecastCategoryRuleRow,
  ForecastRuleCell,
  ForecastScenario,
} from "../../hooks/useForecastCategoryRules";
import { useForecastCategoryRules } from "../../hooks/useForecastCategoryRules";
import { buildParentCategoryGroups } from "@/utils/categoryParentGroups";

function isExcludedForecastCategory(name: string): boolean {
  return name.trim().toLowerCase() === "other expenses";
}

function ChevronToggle({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={`size-4 shrink-0 text-slate-500 transition-transform ${expanded ? "" : "-rotate-90"}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
}

function defaultScenarioCells(): Record<ForecastScenario, ForecastRuleCell> {
  const mk = (): ForecastRuleCell => ({
    recurrenceType: "average_last_x_months",
    lookbackMonths: 6,
    yearlyGrowthRate: null,
    isActive: true,
    isDefault: true,
  });
  return { bad: mk(), mid: mk(), good: mk() };
}

type ForecastCategoryGroup = {
  parent: ForecastCategoryRuleRow;
  parentMeta?: CategoryItem;
  children: ForecastCategoryRuleRow[];
  syntheticParent: boolean;
};

/** Reuses shared parent/subcategory grouping used by cashflow table. */
function buildForecastCategoryGroups(
  rows: ForecastCategoryRuleRow[],
  categoriesById: Map<string, CategoryItem>,
  type: "income" | "expense",
): ForecastCategoryGroup[] {
  return buildParentCategoryGroups(
    rows,
    categoriesById,
    type,
    (row) => row.categoryId,
    (parentMeta) => ({
      categoryId: parentMeta.id,
      categoryName: parentMeta.name,
      categoryType: type,
      scenarios: defaultScenarioCells(),
    }),
  );
}

function ruleToken(cell: ForecastRuleCell) {
  if (cell.recurrenceType === "copy_last_month") {
    return { icon: Copy, value: "Copy", label: "Copy last month" };
  }
  if (cell.recurrenceType === "yearly_percentage_growth") {
    const pct = Math.round((cell.yearlyGrowthRate || 0) * 100);
    return { icon: TrendingUp, value: `${pct}%`, label: "Yearly growth" };
  }
  return {
    icon: Waves,
    value: `${cell.lookbackMonths || 6}`,
    label: "Average months",
  };
}

export default function ForecastSettingsPage() {
  const { currency } = useCurrentCurrency();
  const { effectiveConsolidated } = useConsolidated();
  const { categories } = useCategories();
  const categoryRules = useForecastCategoryRules(currency);
  const projection = useForecastProjection("mid", currency, {
    consolidated: effectiveConsolidated,
  });
  const {
    insights: insightData,
    isLoading: insightsLoading,
    refresh: refreshInsights,
  } = useForecastInsights();
  const [editing, setEditing] = useState<{
    categoryId: string;
    categoryName: string;
    scenario: ForecastScenario;
    cell: ForecastRuleCell;
  } | null>(null);

  const [incomeOpen, setIncomeOpen] = useState(true);
  const [expensesOpen, setExpensesOpen] = useState(true);
  const [openCategoryGroups, setOpenCategoryGroups] = useState<
    Record<string, boolean>
  >({});

  function isGroupOpen(groupKey: string): boolean {
    return openCategoryGroups[groupKey] ?? true;
  }

  function setGroupOpen(groupKey: string, next: boolean): void {
    setOpenCategoryGroups((prev) => ({ ...prev, [groupKey]: next }));
  }

  useEffect(() => {
    void refreshInsights(projection.scenario, currency).catch(() => {
      // Keep page usable if insights fetch fails.
    });
  }, [projection.scenario, refreshInsights, currency, effectiveConsolidated]);

  const categoryColorById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of categories) {
      const hex = c.color?.trim();
      if (hex) m[c.id] = hex;
    }
    return m;
  }, [categories]);

  const categoryParentById = useMemo(() => {
    const m: Record<string, string | null> = {};
    for (const c of categories) {
      m[c.id] = c.parentCategoryId ?? null;
    }
    return m;
  }, [categories]);

  const categoryLabelById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of categories) {
      m[c.id] = c.name;
    }
    return m;
  }, [categories]);

  const groupedRows = useMemo(() => {
    const visible = categoryRules.rows.filter(
      (row) => !isExcludedForecastCategory(row.categoryName),
    );
    return {
      income: visible.filter((row) => row.categoryType === "income"),
      expense: visible.filter((row) => row.categoryType !== "income"),
    };
  }, [categoryRules.rows]);

  const categoriesById = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories],
  );

  const incomeGroups = useMemo(
    () =>
      buildForecastCategoryGroups(groupedRows.income, categoriesById, "income"),
    [groupedRows.income, categoriesById],
  );

  const expenseGroups = useMemo(
    () =>
      buildForecastCategoryGroups(
        groupedRows.expense,
        categoriesById,
        "expense",
      ),
    [groupedRows.expense, categoriesById],
  );

  function renderRuleCells(row: ForecastCategoryRuleRow) {
    const typeLabel = row.categoryType === "income" ? "Income" : "Expense";
    const typeCellTone =
      row.categoryType === "income"
        ? "bg-emerald-50/80 text-emerald-900"
        : "bg-rose-50/80 text-rose-900";

    return (
      <>
        <td
          className={`border-b border-slate-100 px-3 py-2.5 text-xs font-medium ${typeCellTone}`}
        >
          {typeLabel}
        </td>
        {(["bad", "mid", "good"] as const).map((scenario) => {
          const cell = row.scenarios[scenario];
          const token = ruleToken(cell);
          const Icon = token.icon;
          return (
            <td
              key={scenario}
              className="border-b border-slate-100 px-3 py-2.5"
            >
              <button
                type="button"
                title={token.label}
                onClick={() =>
                  setEditing({
                    categoryId: row.categoryId,
                    categoryName: row.categoryName,
                    scenario,
                    cell,
                  })
                }
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm transition ${
                  cell.isDefault
                    ? "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100"
                    : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                }`}
                disabled={categoryRules.isLoading}
                aria-label={`Edit ${row.categoryName} ${scenario} rule`}
              >
                <Icon className="h-4 w-4" />
                <span>{token.value}</span>
              </button>
            </td>
          );
        })}
      </>
    );
  }

  function renderCategoryGroups(
    groups: ForecastCategoryGroup[],
    type: "income" | "expense",
  ) {
    return groups.map((group) => {
      const groupKey = `${type}:${group.parent.categoryId}`;
      const isOpen = isGroupOpen(groupKey);
      const hasChildren = group.children.length > 0;

      return (
        <Fragment key={group.parent.categoryId}>
          <tr className="text-slate-700">
            <td className="border-b border-slate-100 py-2.5 pl-4 pr-3">
              <span className="flex items-center gap-2 font-normal text-slate-600">
                {hasChildren ? (
                  <button
                    type="button"
                    onClick={() => setGroupOpen(groupKey, !isOpen)}
                    className="inline-flex items-center"
                    aria-expanded={isOpen}
                    title="Toggle subcategories"
                  >
                    <ChevronToggle expanded={isOpen} />
                  </button>
                ) : (
                  <span className="inline-block w-4 shrink-0" aria-hidden />
                )}
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{
                    backgroundColor: group.parentMeta?.color ?? "#94a3b8",
                  }}
                />
                <span className="font-medium text-slate-800">
                  {group.parent.categoryName}
                </span>
              </span>
            </td>
            {renderRuleCells(group.parent)}
          </tr>
          {hasChildren && isOpen
            ? group.children.map((child) => {
                const childMeta = categoriesById.get(child.categoryId);
                return (
                  <tr key={child.categoryId} className="text-slate-700">
                    <td className="border-b border-slate-100 py-2.5 pl-10 pr-3">
                      <span className="flex items-center gap-2 font-normal text-slate-600">
                        <span
                          className="inline-block w-4 shrink-0"
                          aria-hidden
                        />
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{
                            backgroundColor: childMeta?.color ?? "#94a3b8",
                          }}
                        />
                        <span className="font-medium text-slate-800">
                          {child.categoryName}
                        </span>
                      </span>
                    </td>
                    {renderRuleCells(child)}
                  </tr>
                );
              })
            : null}
        </Fragment>
      );
    });
  }

  const sectionHeaderClass =
    "border-b border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600";

  return (
    <div className="mx-auto p-10 space-y-6">
      <p className="text-xs text-slate-500">
        {effectiveConsolidated
          ? `All transactions are included (consolidated view). Amounts use your selected display currency (${currency}); switching currency converts values using ECB reference rates.`
          : `Showing ${currency} transactions only. Switch to consolidated mode in the header to include all currencies.`}
      </p>
      <ForecastChartPanel
        data={projection.data}
        isLoading={projection.isLoading}
        scenario={projection.scenario}
        onScenarioChange={projection.setScenario}
        currency={currency}
        categoryColorById={categoryColorById}
        categoryParentById={categoryParentById}
        categoryLabelById={categoryLabelById}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold text-slate-900">
                Category scenarios
              </h3>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1">
                  <Waves className="h-3.5 w-3.5" /> Avg
                </span>
                <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1">
                  <TrendingUp className="h-3.5 w-3.5" /> %
                </span>
                <span
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1"
                  title="Last month (copy prior month amount)"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-separate border-spacing-0 text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-slate-500">
                    <th className="border-b border-slate-200 px-3 py-2 text-left font-medium">
                      Category
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2 text-left font-medium">
                      Type
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2 text-left font-medium">
                      Bad
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2 text-left font-medium">
                      Mid
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2 text-left font-medium">
                      Good
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-white">
                    <td colSpan={5} className={sectionHeaderClass}>
                      <button
                        type="button"
                        onClick={() => setIncomeOpen(!incomeOpen)}
                        className="flex w-full items-center gap-2 text-left"
                        aria-expanded={incomeOpen}
                      >
                        <ChevronToggle expanded={incomeOpen} />
                        Income
                      </button>
                    </td>
                  </tr>
                  {incomeOpen
                    ? renderCategoryGroups(incomeGroups, "income")
                    : null}

                  <tr className="bg-white">
                    <td colSpan={5} className={sectionHeaderClass}>
                      <button
                        type="button"
                        onClick={() => setExpensesOpen(!expensesOpen)}
                        className="flex w-full items-center gap-2 text-left"
                        aria-expanded={expensesOpen}
                      >
                        <ChevronToggle expanded={expensesOpen} />
                        Expenses
                      </button>
                    </td>
                  </tr>
                  {expensesOpen
                    ? renderCategoryGroups(expenseGroups, "expense")
                    : null}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <SmartSuggestionsPanel
            insights={insightData}
            isLoading={insightsLoading}
          />
        </aside>
      </div>

      <ForecastRuleEditorModal
        isOpen={Boolean(editing)}
        categoryName={editing?.categoryName || ""}
        scenario={editing?.scenario || "mid"}
        initialCell={editing?.cell || null}
        isSaving={categoryRules.isSaving}
        onClose={() => setEditing(null)}
        onSave={async (payload) => {
          if (!editing) return;
          try {
            await categoryRules.upsertRule(
              editing.categoryId,
              editing.scenario,
              payload,
            );
            await projection.refresh();
            void refreshInsights(projection.scenario, currency);
            setEditing(null);
            toast.success("Scenario rule updated");
          } catch (error) {
            toast.error(
              (error as Error)?.message || "Failed to update scenario rule",
            );
          }
        }}
        onClear={async () => {
          if (!editing) return;
          try {
            await categoryRules.clearRule(editing.categoryId, editing.scenario);
            await projection.refresh();
            setEditing(null);
            toast.success("Rule reset to default");
          } catch (error) {
            toast.error((error as Error)?.message || "Failed to reset rule");
          }
        }}
      />
    </div>
  );
}
